import {graphql, useFragment} from 'react-relay'
import type {LinkedPullRequest$data, LinkedPullRequest$key} from './__generated__/LinkedPullRequest.graphql'
import {ActionList, Link, Octicon} from '@primer/react'
import {issueLinkedPullRequestHovercardPath} from '@github-ui/paths'
import {PullRequestStateIcons} from '@github-ui/timeline-items'

const LinkedPullRequestPull = graphql`
  fragment LinkedPullRequest on PullRequest {
    url
    number
    state
    repository {
      name
      nameWithOwner
      owner {
        login
      }
    }
  }
`

export const LinkedPullRequest = ({
  pullKey,
  issueRepositoryName,
  isListItem,
  itemKey,
  isSticky,
}: {
  pullKey: LinkedPullRequest$key
  issueRepositoryName: string
  isListItem?: boolean
  itemKey?: string
  isSticky?: boolean
}) => {
  const pull = useFragment(LinkedPullRequestPull, pullKey)
  const owner = pull?.repository?.owner?.login
  const repoName = pull?.repository?.name

  const org = owner.length > 10 ? `${owner.slice(0, 10)}...` : owner
  const repo = repoName.length > 10 ? `${repoName.slice(0, 10)}...` : repoName

  const isExternal = pull.repository.nameWithOwner.toLocaleLowerCase() !== issueRepositoryName.toLocaleLowerCase()
  const {icon, color} = getPrIconColor(pull)

  const title = isExternal ? `${org}/${repo}#${pull?.number}` : `#${pull?.number}`

  const hoverCard = isSticky
    ? null
    : issueLinkedPullRequestHovercardPath({
        owner: pull.repository.owner.login,
        repo: pull.repository.name,
        pullRequestNumber: pull.number,
      })

  if (isListItem) {
    return (
      <ActionList.LinkItem href={pull.url} key={itemKey} target="_blank">
        <ActionList.LeadingVisual>
          <Octicon icon={icon} sx={{color}} />
        </ActionList.LeadingVisual>
        {title}
      </ActionList.LinkItem>
    )
  }

  return (
    <Link
      sx={{
        fontSize: 0,
        overflow: isExternal ? 'hidden' : 'visible',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
      }}
      href={pull.url}
      data-hovercard-url={hoverCard}
      target="_blank"
    >
      {title}
    </Link>
  )
}

const getPrIconColor = (pullRequest: LinkedPullRequest$data) => {
  const state = pullRequest.state === 'MERGED' ? 'MERGED' : 'OPEN'
  const {color, icon} = PullRequestStateIcons[state]!
  return {color, icon}
}

try{ LinkedPullRequest.displayName ||= 'LinkedPullRequest' } catch {}